import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { brandHome } from '../../brandHomeTokens';
import { SEVERITY_TONES } from '../../severityTones';
import type { StoreViewModel } from '../../storeViewModel';

interface ManyStoreLayoutProps {
  stores: StoreViewModel[];
}

const MAX_TRIAGE_ROWS = 10;

export const ManyStoreLayout: React.FC<ManyStoreLayoutProps> = ({ stores }) => {
  // Bucketed by worst issue severity, not just `!lastSyncAt` — a store with a
  // recorded lastSyncAt that is older than cadence x 3 is still blocking.
  // Named by severity, not by theme ("stale"/"with issues") — a warning-level
  // store can be a MAP violation or a large exclusion, not just staleness, so
  // a thematic label would misdescribe most of the rows in that bucket.
  // Unfinished setup wins over MAP/sync issue buckets — a dealer still
  // connecting should read as "setting up", not a warning with no setup context.
  // MAP for those stores still surfaces on the few-store card and attention queue.
  const settingUpStores = stores.filter((s) => s.setupIncomplete);
  const blockingStores = stores.filter(
    (s) => !s.setupIncomplete && s.worstIssueSeverity === 'blocking'
  );
  const warningStores = stores.filter(
    (s) => !s.setupIncomplete && s.worstIssueSeverity === 'warning'
  );
  const healthy = stores.length - blockingStores.length - warningStores.length - settingUpStores.length;

  const attentionNeeded = blockingStores.length + warningStores.length;
  const triage = [...blockingStores, ...warningStores].slice(0, MAX_TRIAGE_ROWS);
  // Healthy stores are never in `triage`, so they are always fully hidden;
  // separately, an attention-needing store can also be cut off by the cap.
  const hiddenAttentionRows = Math.max(0, attentionNeeded - MAX_TRIAGE_ROWS);

  return (
    <>
      <Box sx={{ p: '14px 20px', borderBottom: `1px solid ${brandHome.color.hairline}`, display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: SEVERITY_TONES.positive.ink }}>{healthy} healthy</Typography>
        <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: SEVERITY_TONES.info.ink }}>{settingUpStores.length} setting up</Typography>
        <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: SEVERITY_TONES.warning.ink }}>{warningStores.length} warning</Typography>
        <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: SEVERITY_TONES.blocking.ink }}>{blockingStores.length} blocking</Typography>
        <Typography sx={{ ml: 'auto', fontSize: '12.5px', color: brandHome.color.muted }}>{stores.length} stores</Typography>
      </Box>
      <Box sx={{ p: '11px 20px', bgcolor: brandHome.color.subtleSurface, fontSize: '11px', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: brandHome.color.faint }}>
        Needs your attention first
      </Box>
      {triage.map((store) => {
        const isBlocking = store.worstIssueSeverity === 'blocking';
        const tone = isBlocking ? SEVERITY_TONES.blocking : SEVERITY_TONES.warning;
        return (
          <Box
            key={store.storeId}
            sx={{ display: 'flex', alignItems: 'center', gap: '12px', p: '11px 20px', borderBottom: `1px solid ${brandHome.color.rowHairline}`, flexWrap: 'wrap' }}
          >
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tone.dot, flex: '0 0 auto' }} />
            <Typography sx={{ fontSize: '13.5px', fontWeight: 600, flex: '1 1 190px' }}>{store.storeName}</Typography>
            <Typography sx={{ fontSize: '10.5px', fontWeight: 700, textTransform: 'uppercase', color: brandHome.color.faint, flex: '0 0 92px' }}>
              {store.includedProductCount.toLocaleString()} listed
            </Typography>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, flex: '0 0 104px' }}>
              {store.lastSyncAt ? new Date(store.lastSyncAt).toLocaleDateString() : 'Never'}
            </Typography>
            <Chip
              label={isBlocking ? 'Blocking' : 'Warning'}
              size="small"
              sx={{ bgcolor: tone.bg, color: tone.ink, fontWeight: 700, fontSize: '11.5px' }}
            />
          </Box>
        );
      })}
      <Box sx={{ p: '12px 20px' }}>
        <Typography sx={{ fontSize: '12.5px', color: brandHome.color.muted }}>
          Sorted by severity. {healthy} healthy store{healthy === 1 ? '' : 's'} {healthy === 1 ? 'is' : 'are'} hidden.
          {settingUpStores.length > 0 &&
            ` ${settingUpStores.length} store${settingUpStores.length === 1 ? ' is' : 's are'} still setting up.`}
          {hiddenAttentionRows > 0 &&
            ` ${hiddenAttentionRows} more store${hiddenAttentionRows === 1 ? '' : 's'} need${hiddenAttentionRows === 1 ? 's' : ''} attention.`}
        </Typography>
      </Box>
    </>
  );
};
